import { yupResolver } from '@hookform/resolvers/yup';
import fetchJsonp from 'fetch-jsonp';
import { delay, has, isEmpty, isEqual, isNil, startsWith } from 'lodash';
import Head from 'next/head';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import HomepageFooter from 'components/homepage-footer';
import HomepageHeader from 'components/homepage-header';
import useMedia from 'hooks/useMedia';
import useTriggerFunnelyticsEvent from 'hooks/useTriggerFunnelyticsEvent';
import { HOME_VALUATION_EVENTS } from 'hooks/useTriggerFunnelyticsEvent/constants';
import { AppDispatch } from 'store';
import { zohoHomeValuationThunk } from 'store/slices/homeValuation/thunks';
import { Fields } from 'types/fields';
import { homeValuationSchema } from 'utils/validations';
import { IGoogleData } from '../google-places/useGooglePlaces';
import {
  RPR_CBCODE,
  RPR_TOKEN,
  RPR_WIDGET_URL,
  media,
  schemaJson,
} from './constants';
import messages from './messages';
import Styled from './styles';
import ValuationForm from './valuation-form';
import ValuationFormMobile from './valuation-form/valuationFormMobile';
import ValuationResult from './valuation-result';

const defaultValues = {
  first_name: '',
  last_name: '',
  address: '',
  email: '',
  phone_number: '',
};

function HomeValuation() {
  const { isBelowSmallLaptop } = useMedia();
  const { triggerEvent } = useTriggerFunnelyticsEvent();
  const [widgetError, setWidgetError] = useState(false);
  const [price, setPrice] = useState<string | null>(null);
  const [chartUrl, setChartUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasWidgetLoaded, setHasWidgetLoaded] = useState(false);
  const [widgetHTML, setWidgetHTML] = useState('');
  const [googleData, setGoogleData] = useState<
    (IGoogleData & { full: string }) | Record<string, never>
  >({});
  const widgetRef = useRef() as React.MutableRefObject<HTMLDivElement>;
  const dispatch = useDispatch<AppDispatch>();

  const [callbackRequested, setCallbackRequested] = useState<string[]>([]);
  const [cmaRequested, setCMARequested] = useState<Record<string, string[]>>(
    {}
  );

  const homeValuationForm = useForm<Fields>({
    mode: 'onTouched',
    resolver: yupResolver(homeValuationSchema),
    defaultValues,
  });

  const isSectionOne = !hasWidgetLoaded && !widgetError;
  const isSectionTwo = hasWidgetLoaded || widgetError;
  const isMobileStepOne = isBelowSmallLaptop && isEmpty(googleData?.state);
  const isMobileStepTwo = isBelowSmallLaptop && !isEmpty(googleData?.state);

  useEffect(() => {
    window.sessionStorage.setItem('resetUtmParams', 'true');
  }, []);

  useEffect(() => {
    if (hasWidgetLoaded) {
      processResults();
    } else {
      setPrice(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasWidgetLoaded]);

  function onCallbackRequest(newLeadId: string) {
    setCallbackRequested((prevState) => [...prevState, newLeadId]);
  }

  function onCMARequest(newLeadId: string, newEmail: string) {
    setCMARequested((prevState) => ({
      ...prevState,
      [newLeadId]: [
        ...(has(prevState, newLeadId) ? prevState[newLeadId] : []),
        newEmail,
      ],
    }));
  }

  function sendLeads(homePrice: string) {
    const { getValues } = homeValuationForm;
    const formData = getValues();
    const eventName =
      HOME_VALUATION_EVENTS[
        startsWith(homePrice, 'rprError') ? 'SUBMIT_WITHOUT_AVM_DATA' : 'SUBMIT'
      ];

    triggerEvent({
      eventName,
      data: {
        name: `${formData.first_name} ${formData.last_name}`,
        address: formData.address,
        phone_number: formData.phone_number,
        email: formData.email,
      },
    });

    const { street1, city, state, zip } = googleData;

    dispatch(
      zohoHomeValuationThunk({
        ...formData,
        street: street1,
        city,
        state,
        zip_code: zip,
        home_price: homePrice,
        country: 'US',
      })
    );
  }

  function processResults() {
    const parser = new DOMParser();
    const doc = parser.parseFromString(widgetHTML, 'text/html');

    const finalPrice = doc.querySelector('.rprw-est-value');
    const chartImageUrl = (
      doc.querySelector('.rprw-chart-cont img') as HTMLImageElement
    )?.src;

    if (!isNil(finalPrice) && finalPrice.hasChildNodes()) {
      const priceDiv = finalPrice.childNodes[0];
      const priceText = priceDiv.textContent;

      if (priceText) {
        setPrice(priceText);
        sendLeads(priceText.replace(/,/g, '').slice(1));
      }
    }

    if (!isNil(chartImageUrl)) {
      setChartUrl(chartImageUrl);
    }
  }

  function onSubmit(data: Fields) {
    const { address } = data;

    setLoading(true);
    setWidgetError(false);
    setHasWidgetLoaded(false);
    setWidgetHTML('');
    setPrice(null);

    const rprUrlParams = new URLSearchParams({
      Token: RPR_TOKEN,
      Query: address,
      cbcode: RPR_CBCODE,
      ShowRprLinks: 'false',
    });

    fetchJsonp(`${RPR_WIDGET_URL}?${rprUrlParams.toString()}`)
      .then((res) => res.json())
      .then((jsonResult) => {
        if (isEqual(jsonResult.status, 0)) {
          setHasWidgetLoaded(true);
          setWidgetHTML(jsonResult.html);
        } else {
          setWidgetError(true);
          sendLeads(`rprError: ${jsonResult.statusText}`);
        }
      })
      .catch(() => {
        setWidgetError(true);
        sendLeads('rprError');
      })
      .finally(() => setLoading(false));
  }

  function scrollToWidget() {
    widgetRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }

  function getHeaderText() {
    if (isBelowSmallLaptop) {
      if (isMobileStepOne) {
        return messages.headerOneForMobile;
      }

      return messages.headerTwoForMobile;
    }

    return messages.header;
  }

  function resetAddress() {
    const { getValues } = homeValuationForm;
    const address = getValues('address');

    setGoogleData({ full: address });
    setHasWidgetLoaded(false);
    setWidgetError(false);

    delay(() => {
      (
        document.querySelector('.autocomplete-input') as HTMLInputElement
      )?.focus();
    }, 100);
  }

  return (
    <Styled.ValuationWrapper isSectionTwo={isSectionTwo}>
      <Head>
        <title>Free Home Valuation</title>
        <meta
          name="description"
          content="Get a free estimate of your home's value with Richr's home valuation tool. Our proprietary algorithm analyzes data from recent home sales in your area to provide you with an accurate valuation."
        />
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaJson) }}
        />
      </Head>
      <HomepageHeader isWhiteLinks={isSectionOne} />
      <Styled.Heading hidden={isSectionTwo} isMobileStepTwo={isMobileStepTwo}>
        <FormattedMessage
          {...getHeaderText()}
          values={{ rsquo: <>&rsquo;</> }}
        />
      </Styled.Heading>
      {isBelowSmallLaptop && !isMobileStepTwo && !hasWidgetLoaded && (
        <ValuationFormMobile
          setGoogleData={setGoogleData}
          homeValuationForm={homeValuationForm}
        />
      )}
      {isSectionTwo && (
        <ValuationResult
          address={googleData?.full}
          price={price}
          chartUrl={chartUrl}
          onEdit={resetAddress}
          homeValuationForm={homeValuationForm}
          isWidgetError={widgetError}
          callbackRequested={callbackRequested}
          onCallbackRequest={onCallbackRequest}
          cmaRequested={cmaRequested}
          onCMARequest={onCMARequest}
        />
      )}
      {isSectionOne && (
        <Styled.FormAndWidgetWrapper>
          <Styled.FormWrapper hidden={isMobileStepOne}>
            <ValuationForm
              onSubmit={onSubmit}
              widgetError={widgetError}
              price={price}
              loading={loading}
              setGoogleData={setGoogleData}
              scrollToWidget={scrollToWidget}
              hasWidgetLoaded={hasWidgetLoaded}
              homeValuationForm={homeValuationForm}
              address={googleData?.full}
              onBack={resetAddress}
            />
          </Styled.FormWrapper>
          <div hidden={isMobileStepTwo}>
            {hasWidgetLoaded ? (
              <Styled.WidgetSection
                id="widgetWrapper"
                hasWidgetLoaded={hasWidgetLoaded}
                dangerouslySetInnerHTML={{ __html: widgetHTML }}
                ref={widgetRef}
              />
            ) : (
              <Styled.WidgetSection
                id="widgetWrapper"
                hasWidgetLoaded={hasWidgetLoaded}
              >
                <Styled.WidgetOverlayImage>
                  <Image src={media.lockIcon} width={31} height={31} alt="" />
                </Styled.WidgetOverlayImage>
                <Image
                  src={media.reportBlur}
                  width={350}
                  height="100%"
                  alt="report-preview"
                />
              </Styled.WidgetSection>
            )}
          </div>
        </Styled.FormAndWidgetWrapper>
      )}
      {isSectionTwo && <HomepageFooter />}
    </Styled.ValuationWrapper>
  );
}

export default HomeValuation;
